import { useState, useMemo } from 'react';

/**
 * Reusable hook for transferring data between components via native HTML5 drag-and-drop.
 * 
 * @param {string} mimeType - Custom MIME type to scope drag/drop (e.g. 'application/my-data')
 * @param {object} options
 * @param {function} options.onDrop - Called with parsed data when something is dropped
 * @returns {{ draggableProps: (data: any) => object, dropZoneProps: object, isDragOver: boolean, dropZoneStyle: object }}
 */
export default function useDragTransfer(mimeType, { onDrop } = {}) {
  const [isDragOver, setIsDragOver] = useState(false);

  const draggableProps = (data) => ({
    draggable: true,
    onMouseDown: (e) => e.stopPropagation(),
    onDragStart: (e) => {
      e.dataTransfer.setData(mimeType, JSON.stringify(data));
    },
    onDragEnd: () => setIsDragOver(false),
    style: { cursor: 'grab' },
  });

  const dropZoneProps = useMemo(() => ({
    onDragOver: (e) => {
      if (e.dataTransfer.types.includes(mimeType)) {
        e.preventDefault();
        setIsDragOver(true);
      }
    },
    onDragLeave: () => setIsDragOver(false),
    onDrop: (e) => {
      e.preventDefault();
      setIsDragOver(false);
      try {
        const data = JSON.parse(e.dataTransfer.getData(mimeType));
        onDrop?.(data);
      } catch (err) {}
    },
  }), [mimeType, onDrop]);

  const dropZoneStyle = isDragOver
    ? { outline: '2px dashed #007bff', borderRadius: '4px' }
    : undefined;

  return { draggableProps, dropZoneProps, dropZoneStyle, isDragOver };
}
